import {
	ExperimentOutlined,
	HeartOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { Card, List, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ACTIVITY_COLORS, ACTIVITY_TYPES } from '@/constants/dashboard';
import type { DashboardStats } from '@/types';

const { Text } = Typography;

interface RecentActivityProps {
	stats: DashboardStats;
}

interface ActivityStyle {
	icon: React.ReactNode;
	color: string;
}

const RecentActivity = ({ stats }: RecentActivityProps) => {
	const { t } = useTranslation('components');

	const getActivityStyle = (type: string): ActivityStyle => {
		switch (type) {
			case ACTIVITY_TYPES.USER_REGISTERED:
				return {
					icon: <UserOutlined />,
					color: ACTIVITY_COLORS[ACTIVITY_TYPES.USER_REGISTERED],
				};
			case ACTIVITY_TYPES.PET_REGISTERED:
				return {
					icon: <HeartOutlined />,
					color: ACTIVITY_COLORS[ACTIVITY_TYPES.PET_REGISTERED],
				};
			case ACTIVITY_TYPES.AI_SCAN:
				return {
					icon: <ExperimentOutlined />,
					color: ACTIVITY_COLORS[ACTIVITY_TYPES.AI_SCAN],
				};
			default:
				return {
					icon: <UserOutlined />,
					color: '#10b981',
				};
		}
	};

	const hasActivity = stats.recentActivity && stats.recentActivity.length > 0;

	return (
		<Card
			title={
				<span className='font-semibold text-slate-700'>
					{t('dashboard.recentActivity.title')}
				</span>
			}
			className='h-full border border-slate-100 shadow-sm rounded-xl'
			styles={{
				body: { padding: '0 16px 16px' },
				header: { borderBottom: '1px solid #f1f5f9', padding: '0 16px' },
			}}
		>
			{hasActivity ? (
				<List
					itemLayout='horizontal'
					dataSource={stats.recentActivity}
					rowKey='id'
					className='w-full'
					renderItem={(item) => {
						const style = getActivityStyle(item.type);
						return (
							<List.Item className='!px-0 !py-3 !border-b-slate-50 last:!border-b-0'>
								<div className='flex items-start gap-3 w-full'>
									<div
										className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm'
										style={{ backgroundColor: style.color }}
									>
										{style.icon}
									</div>
									<div className='flex-1 min-w-0'>
										<div className='flex justify-between items-start'>
											<span className='font-medium text-slate-700 text-sm'>
												{item.description}
											</span>
											<span className='text-xs text-slate-400 whitespace-nowrap ml-2'>
												{new Date(item.timestamp).toLocaleDateString()}
											</span>
										</div>
										{(item.userName || item.petName) && (
											<div className='mt-1 text-xs font-medium' style={{ color: style.color }}>
												{item.petName && <span>{item.petName}</span>}
												{item.petName && item.userName && <span> · </span>}
												{item.userName && <span>{item.userName}</span>}
											</div>
										)}
									</div>
								</div>
							</List.Item>
						);
					}}
				/>
			) : (
				<div className='py-8 text-center'>
					<UserOutlined className='text-2xl text-slate-300 mb-2' />
					<Text className='text-sm text-slate-400 block'>
						{t('dashboard.recentActivity.noData')}
					</Text>
				</div>
			)}
		</Card>
	);
};

export { RecentActivity };
export default RecentActivity;
